import { type LoadPriority } from '~/hooks/useLoadPriority';
import { type MicrositeResponsiveImage } from '~/model';
import { useRef } from 'react';
import styled from '@emotion/styled';
import useBreakpointImage from '~/hooks/useBreakpointImage';

export interface MicrositeImageProps {
	priority: LoadPriority;
	image: MicrositeResponsiveImage;
	onLoad?: () => void;
	primary?: boolean;
}

const Image = styled.img<{ blur?: boolean }>`
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: 250ms all linear;

	${({ blur }) => blur && 'filter: blur(1rem); transform: scale(1.05);'}
`;

export default function MicrositeResponsiveImg(props: MicrositeImageProps) {
	if (props.image.small_1x.includes('.svg')) {
		return <Image src={props.image.small_1x} itemProp={props.primary ? 'primaryImageOfPage' : undefined} onLoad={props.onLoad} />;
	}

	return <Responsive {...props} />;
}

function Responsive({ priority, image, primary, onLoad }: MicrositeImageProps) {
	const ref = useRef<HTMLImageElement>(null);
	const { srcSet, placeholder } = useBreakpointImage({
		ref,
		priority,
		url(width, height, dpi) {
			if (width > 1280 || height > 800) {
				return image.large_2x;
			}

			if (width > 960 || height > 640) {
				if (dpi === 1) {
					return image.large_1x;
				}

				return image.large_2x;
			}

			if (width > 480 || height > 320) {
				return image.medium_2x;
			}

			if (width > 192 || height > 128) {
				if (dpi === 1) {
					return image.medium_1x;
				}

				return image.medium_2x;
			}

			if (dpi === 3) {
				return image.small_3x;
			}

			if (dpi === 2) {
				return image.small_2x;
			}

			return image.small_1x;
		},
	});

	return <Image src={placeholder} ref={ref} srcSet={srcSet} itemProp={primary ? 'primaryImageOfPage' : undefined} onLoad={onLoad} />;
}
